/**
 * Inventory Service
 * 
 * Handles all API calls related to inventory management
 */
import { apiService } from './api.service';

// API endpoints
const ENDPOINTS = {
  SUPPLIER_INVENTORY: (sku) => `/inventory/supplier/${sku}`,
  INVENTORY_SUMMARY: '/inventory/summary',
  INVENTORY_DETAILS: (sku) => `/inventory/details/${sku}`,
  LOCATION_INVENTORY: (locationId) => `/inventory/location/${locationId}/onhand`,
  CURRENT_INVENTORY: '/inventory/current'
};

/**
 * Inventory API functions
 */
export const inventoryService = {
  /**
   * Get supplier inventory for a specific SKU
   * @param {string} sku - The SKU to fetch inventory for
   * @returns {Promise<Object>} - Supplier inventory data
   */
  getSupplierInventory: async (sku) => {
    try {
      if (!sku) {
        throw new Error('SKU is required');
      }
      const response = await apiService.get(ENDPOINTS.SUPPLIER_INVENTORY(sku));
      return response;
    } catch (error) {
      console.error('Error fetching supplier inventory:', error);
      throw error;
    }
  },

  /**
   * Get inventory summary
   * @returns {Promise<Object>} - Inventory summary data
   */
  getInventorySummary: async () => {
    try {
      const response = await apiService.get(ENDPOINTS.INVENTORY_SUMMARY);
      return response;
    } catch (error) {
      console.error('Error fetching inventory summary:', error);
      throw error;
    }
  },

  /**
   * Get detailed inventory information for a specific SKU
   * @param {string} sku - The SKU to fetch details for
   * @returns {Promise<Object>} - Detailed inventory data
   */
  getInventoryDetails: async (sku) => {
    try {
      if (!sku) {
        throw new Error('SKU is required');
      }
      const response = await apiService.get(ENDPOINTS.INVENTORY_DETAILS(sku));
      return response;
    } catch (error) {
      console.error('Error fetching inventory details:', error);
      throw error;
    }
  },

  /**
   * Get current inventory data
   * @returns {Promise<Object>} - Current inventory data
   */
  getCurrentInventory: async () => {
    try {
      const response = await apiService.get(ENDPOINTS.CURRENT_INVENTORY);
      return response;
    } catch (error) {
      console.error('Error fetching current inventory:', error);
      throw error;
    }
  },

  /**
   * Get current inventory data for a specific warehouse
   * @param {string} warehouseId - The warehouse ID to filter by
   * @returns {Promise<Object>} - Current inventory data for the warehouse
   */
  getCurrentInventoryByWarehouse: async (warehouseId) => {
    try {
      const response = await apiService.get(`${ENDPOINTS.CURRENT_INVENTORY}?warehouse_id=${warehouseId}`);
      return response;
    } catch (error) {
      console.error('Error fetching warehouse inventory:', error);
      throw error;
    }
  },

  /**
   * Get inventory data for a specific location
   * @param {string} locationId - The location ID to fetch inventory for
   * @returns {Promise<Object>} - Location inventory data including:
   *   - total_skus: Total number of unique SKUs
   *   - total_quantity: Total quantity across all SKUs
   *   - in_stock_skus: Number of SKUs with sufficient stock
   *   - low_stock_skus: Number of SKUs below reorder point
   *   - out_of_stock_skus: Number of SKUs with zero inventory
   *   - items: Array of inventory items with details
   */
  getInventoryByLocation: async (locationId) => {
    try {
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      const response = await apiService.get(ENDPOINTS.LOCATION_INVENTORY(locationId));
      return response;
    } catch (error) {
      console.error('Error fetching location inventory:', error);
      throw error;
    }
  }
}; 